import type { Scene, AbstractMesh, ShadowGenerator } from '@babylonjs/core';
import { MeshBuilder, StandardMaterial, Color3 } from '@babylonjs/core';

export interface ProjectileSpawnOptions {
  id: string;
  damage: number;
  speed: number;
  element: 'water' | 'acid' | 'physical';
  lifetime: number;
}

export interface ActiveProjectile {
  id: string;
  position: { x: number; y: number; z: number };
  velocity: { x: number; y: number; z: number };
  damage: number;
  element: string;
  radius: number;
  lifetime: number;
  age: number;
  mesh?: AbstractMesh;
}

export interface CombatTarget {
  id: string;
  position: { x: number; y: number; z: number };
  radius: number;
  currentHp: number;
  takeDamage(amount: number): void;
}

export interface ProjectileHitResult {
  projectileId: string;
  targetId: string;
  damage: number;
  element: string;
}

export class ProjectileManager {
  private projectiles: ActiveProjectile[] = [];
  private nextId = 0;

  public getActiveCount(): number {
    return this.projectiles.length;
  }

  public spawnProjectile(
    origin: { x: number; y: number; z: number },
    direction: { x: number; y: number; z: number },
    options: ProjectileSpawnOptions,
    scene?: Scene,
    shadowGenerator?: ShadowGenerator
  ): ActiveProjectile {
    const len = Math.hypot(direction.x, direction.y, direction.z) || 1;
    const nx = direction.x / len;
    const ny = direction.y / len;
    const nz = direction.z / len;

    let mesh: AbstractMesh | undefined;
    if (scene) {
      mesh = MeshBuilder.CreateSphere(
        `proj_${this.nextId}`,
        { diameter: 0.5, segments: 12 },
        scene
      );
      mesh.position.set(origin.x, origin.y, origin.z);

      const mat = new StandardMaterial(`proj_mat_${this.nextId}`, scene);
      if (options.element === 'water') {
        mat.diffuseColor = new Color3(0.2, 0.6, 1.0);
        mat.emissiveColor = new Color3(0.1, 0.4, 0.8);
      } else if (options.element === 'acid') {
        mat.diffuseColor = new Color3(0.3, 0.9, 0.2);
        mat.emissiveColor = new Color3(0.2, 0.7, 0.1);
      } else {
        mat.diffuseColor = new Color3(0.9, 0.8, 0.3);
        mat.emissiveColor = new Color3(0.6, 0.5, 0.1);
      }
      mesh.material = mat;

      if (shadowGenerator) {
        shadowGenerator.addShadowCaster(mesh);
      }
    }

    const proj: ActiveProjectile = {
      id: `proj_${options.id}_${++this.nextId}`,
      position: { ...origin },
      velocity: {
        x: nx * options.speed,
        y: ny * options.speed,
        z: nz * options.speed
      },
      damage: options.damage,
      element: options.element,
      radius: 0.4,
      lifetime: options.lifetime,
      age: 0,
      mesh
    };

    this.projectiles.push(proj);
    return proj;
  }

  public update(dt: number, targets: CombatTarget[]): ProjectileHitResult[] {
    const hits: ProjectileHitResult[] = [];
    const remaining: ActiveProjectile[] = [];

    for (const proj of this.projectiles) {
      proj.age += dt;
      if (proj.age >= proj.lifetime) {
        if (proj.mesh) {
          proj.mesh.dispose();
        }
        continue;
      }

      proj.position.x += proj.velocity.x * dt;
      proj.position.y += proj.velocity.y * dt;
      proj.position.z += proj.velocity.z * dt;

      if (proj.mesh) {
        proj.mesh.position.set(proj.position.x, proj.position.y, proj.position.z);
      }

      let hasHit = false;
      for (const target of targets) {
        const dx = proj.position.x - target.position.x;
        const dy = proj.position.y - target.position.y;
        const dz = proj.position.z - target.position.z;
        const dist = Math.hypot(dx, dy, dz);

        if (dist <= proj.radius + target.radius) {
          target.takeDamage(proj.damage);
          hits.push({
            projectileId: proj.id,
            targetId: target.id,
            damage: proj.damage,
            element: proj.element
          });
          if (proj.mesh) {
            proj.mesh.dispose();
          }
          hasHit = true;
          break;
        }
      }

      if (!hasHit) {
        remaining.push(proj);
      }
    }

    this.projectiles = remaining;
    return hits;
  }
}
